/**
 * Tokens de design da Bilíngua — fonte única para tema, CSS vars e Ant Design.
 * Alinhado ao Material Design 3 Expressive do app mobile.
 */
import { DARK_PALETTE } from "./darkPalette";
import { md3Expressive } from "./md3Expressive";

const lightColors = {
  primary: "#4614bb",
  primaryLight: "#6d3dd8",
  primaryDark: "#2e0d7a",
  primaryLighter: "#8b5fe8",
  primaryDarker: "#1a0845",
  primaryAlpha10: "rgba(70, 20, 187, 0.1)",
  primaryAlpha20: "rgba(70, 20, 187, 0.2)",
  secondary: "#ec4899",
  secondaryLight: "#f472b6",
  secondaryDark: "#be185d",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#5f33e1",
  accent: "#8b5cf6",
  background: "#f4f7fa",
  backgroundSecondary: "#e8edf2",
  surface: "#ffffff",
  surfaceSecondary: "#f8fafc",
  surfaceTertiary: "#f1f5f9",
  text: "#1e293b",
  textSecondary: "#64748b",
  textTertiary: "#94a3b8",
  textInverse: "#ffffff",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  white: "#ffffff",
  black: "#0f172a",
  hover: "rgba(70, 20, 187, 0.08)",
  focus: "rgba(95, 51, 225, 0.2)",
  shadow: "rgba(0, 0, 0, 0.1)",

  /** Aliases legados (admin / páginas existentes) */
  layoutBg: "#f5f6fa",
  titleOnPrimary: "#ffffff",
  textOnPrimary: "#ffffff",
  textTitle: "#1e293b",
  textComplement: "#64748b",
  textBase: "#1e293b",
  card: "#ffffff",
  lineInWhite: "#e2e8f0",
  inputBackground: "#ffffff",
  buttonText: "#ffffff",
  boxBase: "#ffffff",
  boxFooter: "#f8fafc",
  tableHeaderBg: "#f9f8ff",
  tableHeaderText: "#1a1340",
  tableRowBorder: "#f8f7ff",
  tableRowHover: "#faf8ff",
  tableCellText: "#444444",
  modalHeaderGradientStart: "#2e0d7a",
  modalHeaderGradientEnd: "#4614bb",
  antBorder: "#e8eaf0",
  antInputFocusRing: "rgba(70, 20, 187, 0.1)",
  whatsappGreen: "#25d366",
  whatsappGreenDark: "#128c7e",
  teacherChipBg: "#f0eefa",
  teacherMeta: "#888888",
  teacherBio: "#666666",
  teacherScheduleText: "#555555",
  teacherScheduleBg: "#f5f6fa",
  teacherScheduleBorder: "#e8eaf0",
  teacherAvatarBg: "#e9e3f8",
} as const;

const darkColors = {
  ...lightColors,
  primary: DARK_PALETTE.primary,
  primaryLight: DARK_PALETTE.primaryLight,
  primaryDark: "#8b5fe8",
  primaryLighter: "#ddd6fe",
  primaryDarker: "#6d28d9",
  primaryAlpha10: "rgba(139, 95, 232, 0.15)",
  primaryAlpha20: "rgba(139, 95, 232, 0.25)",
  secondary: "#f472b6",
  secondaryLight: "#f9a8d4",
  secondaryDark: "#ec4899",
  success: "#34d399",
  warning: "#fbbf24",
  danger: "#f87171",
  info: "#7c4dff",
  accent: DARK_PALETTE.accent,
  background: DARK_PALETTE.background,
  backgroundSecondary: DARK_PALETTE.backgroundSecondary,
  surface: DARK_PALETTE.surface,
  surfaceSecondary: DARK_PALETTE.surfaceSecondary,
  surfaceTertiary: DARK_PALETTE.surfaceTertiary,
  text: DARK_PALETTE.text,
  textSecondary: DARK_PALETTE.textSecondary,
  textTertiary: DARK_PALETTE.textTertiary,
  textInverse: DARK_PALETTE.background,
  border: DARK_PALETTE.border,
  borderLight: DARK_PALETTE.borderLight,
  black: DARK_PALETTE.text,
  hover: "rgba(167, 139, 250, 0.12)",
  focus: "rgba(124, 77, 255, 0.4)",
  shadow: "rgba(0, 0, 0, 0.3)",
  layoutBg: DARK_PALETTE.backgroundSecondary,
  textTitle: DARK_PALETTE.text,
  textComplement: DARK_PALETTE.textSecondary,
  textBase: DARK_PALETTE.text,
  card: DARK_PALETTE.surface,
  lineInWhite: DARK_PALETTE.border,
  inputBackground: DARK_PALETTE.surfaceSecondary,
  boxBase: DARK_PALETTE.surface,
  boxFooter: DARK_PALETTE.surfaceSecondary,
  tableHeaderBg: DARK_PALETTE.surfaceSecondary,
  tableHeaderText: DARK_PALETTE.text,
  tableRowBorder: DARK_PALETTE.border,
  tableRowHover: DARK_PALETTE.surfaceTertiary,
  tableCellText: DARK_PALETTE.textSecondary,
  antBorder: DARK_PALETTE.border,
  antInputFocusRing: "rgba(124, 77, 255, 0.25)",
  teacherChipBg: DARK_PALETTE.surfaceSecondary,
  teacherMeta: DARK_PALETTE.textSecondary,
  teacherBio: DARK_PALETTE.textSecondary,
  teacherScheduleText: DARK_PALETTE.textSecondary,
  teacherScheduleBg: DARK_PALETTE.surfaceSecondary,
  teacherScheduleBorder: DARK_PALETTE.border,
  teacherAvatarBg: DARK_PALETTE.surfaceTertiary,
  modalHeaderGradientStart: "#4c1d95",
  modalHeaderGradientEnd: "#6d28d9",
} as const;

export const tokens = {
  md3: md3Expressive,
  colors: lightColors,
  colorsDark: darkColors,
  font: {
    family: "'Manrope', 'Poppins', sans-serif",
    familyHeading: "'Manrope', sans-serif",
    sizes: {
      xs: 11,
      sm: 14,
      md: 14,
      base: 19,
      lg: 19,
      xl: 24,
      "2xl": 24,
      "3xl": 33,
      "4xl": 33,
      "5xl": 44,
      "6xl": 44,
    },
  },
  fontWeight: {
    body: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  layout: {
    contentMaxPx: 1200,
    contentStandardPx: 1200,
    headerFooterInnerMaxPx: 1200,
    mobileFirstExpandPx: 900,
    appShellNarrowMaxPx: 500,
    appShellWideMaxPx: 1200,
    radiusSm: "8px",
    radiusMd: "12px",
    radiusLg: "16px",
    radiusXxl: "28px",
  },
  spacing: {
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    footerAdMarginY: "10px",
  },
  borderRadius: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 28,
    full: 9999,
  },
  shadows: {
    card: "0 2px 12px rgba(82, 54, 152, 0.08)",
    cardHover: "0 8px 28px rgba(82, 54, 152, 0.14)",
    headerAvatar: "0 2px 4px rgba(0, 0, 0, 0.05)",
    headerAvatarHover: "0 4px 8px rgba(0, 0, 0, 0.1)",
    md3Level1: md3Expressive.elevation.level1,
    md3Level2: md3Expressive.elevation.level2,
    md3Level3: md3Expressive.elevation.level3,
  },
  accessibility: {
    minTouchTarget: 44,
    minContrastRatio: 4.5,
  },
  gradients: {
    primary: ["#4614BB", "#6D3DD8", "#8B5FE8"],
    purplePink: ["#4614BB", "#EC4899"],
  },
} as const;

export type AppTokens = typeof tokens;
export type ThemeColors = { [K in keyof typeof lightColors]: string };
